import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IAmplitudeLeadFormProductData} from "../../application/tracking/amplitude/get_amplitude_lead_form_products";
import {
    IGetAmplitudeLeadFormStartEventPropertiesParams,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../application/tracking/amplitude/track_amplitude_lead_form_events";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {AmplitudeAuthLocation} from "../../auth/tracking/track_amplitude_auth_event";
import {AmplitudeReservationButtonName, trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {AmplitudeReservationModalName, trackAmplitudeViewModal} from "../../tracking/amplitude/track_amplitude_view_modal";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {getAmplitudeScreenLocation} from "../../user/tracking/track_amplitude_phone_verified";
import {ViewType} from "../../view_type/ViewType";
import {RESERVATION_CONFIRMED_BUTTON_LABEL} from "../constants/reservation_copy";
import {getReservationEntry} from "../constants/reservation_placement_config";
import {IReservationError} from "../types/IReservationError";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationStepName} from "../types/ReservationStepName";
import {getReservationAuthPlacement} from "./get_reservation_auth_placement";
import {gtmReservation, gtmReservationStart} from "./gtm_reservation";

/*
 * Every step of the free reservation funnel is reported to Amplitude and GTM by one call.
 * The application is created by the backend on confirm, so the lead form events are reported here and not by the lead form.
 * A reservation started from the unlock choice modal is reported by track_unlock_choice_reservation_events instead - get_reservation_tracker picks the funnel.
 */

const RESERVATION_SOURCE = ApplicationSource.PropertyVoxOnlineReservation;
const RESERVATION_SOURCE_SECTION = ApplicationSourceSection.MODAL;
const RESERVATION_CTA_TEXT = InquiryButtonTypeName.FREE_RESERVATION;

export interface IReservationTrackingContext {
    // the placement of the registration that led to this funnel; when omitted, the placement of the reservation button is used
    authPlacement?: AmplitudeAuthPlacement | null;
    // the location the registration flow was opened with, reported by the unlock choice funnel instead of the location of the view type
    amplitudeLocation?: AmplitudeAuthLocation;
    viewType?: ViewType | null;
    // vendor, offer and property details for `products` of the lead form events - without them `products` is empty
    leadData?: IAmplitudeLeadFormProductData | null;
}

export interface IReservationStartTrackingContext extends IReservationTrackingContext {
    // reported by pre_form_open of the buttons on the property page
    offerName?: string | null;
}

type ReservationLeadFormParams = Pick<IGetAmplitudeLeadFormStartEventPropertiesParams, "trackingContext" | "leadData" | "source" | "sourceSection">;

const getAuthPlacement = (request: IReservationRequest, context: IReservationTrackingContext): AmplitudeAuthPlacement | null =>
    getReservationAuthPlacement(request.placement, context.authPlacement);

// which flow and which button the funnel was started from - on every Amplitude event of the funnel
const getReservationFunnelProperties = (request: IReservationRequest) => ({
    reservation_entry: getReservationEntry(request),
    placement: request.placement
});

const trackReservationButtonClick = (
    buttonName: AmplitudeReservationButtonName,
    request: IReservationRequest,
    context: IReservationTrackingContext,
    value: string | null = null
) => {
    trackAmplitudeButtonClick({
        buttonName,
        buttonCategory: "rp_reservation",
        itemId: request.offerId,
        propertyId: request.propertyId,
        value,
        additionalEventProperties: {
            ...getReservationFunnelProperties(request),
            vendor_id: request.vendorId,
            auth_placement: getAuthPlacement(request, context)
        }
    });
};

const trackReservationModalView = (modalName: AmplitudeReservationModalName, request: IReservationRequest, context: IReservationTrackingContext) => {
    trackAmplitudeViewModal({
        modalName,
        location: getAmplitudeScreenLocation(context.viewType ?? null),
        authPlacement: getAuthPlacement(request, context),
        itemId: request.offerId,
        propertyId: request.propertyId,
        additionalEventProperties: {
            ...getReservationFunnelProperties(request),
            vendor_id: request.vendorId
        }
    });
};

// The lead form events of the funnel - none without an offer to report them for
const trackReservationLeadFormEvent = (
    request: IReservationRequest,
    context: IReservationTrackingContext,
    track: (params: ReservationLeadFormParams) => void
) => {
    const trackingContext = getAmplitudeCtaLeadFormTrackingContext({
        source: RESERVATION_SOURCE,
        sourceSection: RESERVATION_SOURCE_SECTION,
        leadData: undefined,
        ctaText: RESERVATION_CTA_TEXT,
        offerId: request.offerId,
        propertyId: request.propertyId,
        viewType: context.viewType ?? null,
        formLocation: "rp_reservation"
    });

    if (trackingContext) {
        track({
            trackingContext: {
                ...trackingContext,
                auth_placement: getAuthPlacement(request, context),
                additionalEventProperties: getReservationFunnelProperties(request)
            },
            leadData: context.leadData ?? undefined,
            source: RESERVATION_SOURCE,
            sourceSection: RESERVATION_SOURCE_SECTION
        });
    }
};

// The click of a reservation button on the property page or in the post-lead modal
export const trackReservationStart = (request: IReservationRequest, context: IReservationStartTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_start", request, context, request.placement);
    gtmReservationStart(request, context.offerName ?? null);
};

// Before the first initiate, so that an initiate failure has a lead_form_start before it
export const trackReservationLeadFormStart = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, trackAmplitudeLeadFormStart);
};

// The first code has been sent
export const trackReservationCodeView = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationModalView("reservation_code", request, context);
    gtmReservation("view", request.placement);
};

// The last digit has been typed in - the code is submitted without a button
export const trackReservationCodeSubmit = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_code_submit", request, context);
    gtmReservation("code_submit", request.placement);
};

// `codesSent` - codes sent before this resend
export const trackReservationCodeResend = (request: IReservationRequest, codesSent: number, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_code_resend", request, context, String(codesSent));
    gtmReservation("resend", request.placement, String(codesSent));
};

// `applicationUuid` - null when the confirm response came without it; the reservation is reported all the same
export const trackReservationGenerateLead = (request: IReservationRequest, applicationUuid: string | null, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, (params) =>
        trackAmplitudeLeadFormGenerateLead({
            ...params,
            ...(applicationUuid === null ? {} : {transactionId: applicationUuid}),
            // the reservation screen collects no marketing consents
            marketingApproval: false,
            inquirySelection: {}
        })
    );

    trackReservationModalView("reservation_confirmed", request, context);
    gtmReservation("success", request.placement);
};

export const trackReservationError = (request: IReservationRequest, error: IReservationError, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, (params) =>
        trackAmplitudeLeadFormError({
            ...params,
            // built from the normalized error - `getAppErrors` would report each of its keys as a form field
            appError: {
                status: typeof error.raw.status === "number" ? error.raw.status : 0,
                fieldErrors: {},
                nonFieldErrors: [error.message]
            },
            errorCode: `${error.kind}:${error.raw.status}`
        })
    );

    gtmReservation("error", request.placement, error.kind);
};

// The close button or Esc of the code step, the overlay or Esc of the confirmation (not its "ROZUMIEM" button)
export const trackReservationClose = (request: IReservationRequest, step: ReservationStepName, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_close", request, context, step);
    gtmReservation("close", request.placement, step);
};

export const trackReservationConfirmedClose = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_confirmed_close", request, context);
    gtmReservation("close", request.placement, RESERVATION_CONFIRMED_BUTTON_LABEL);
};
import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IAmplitudeLeadFormProductData} from "../../application/tracking/amplitude/get_amplitude_lead_form_products";
import {
    IGetAmplitudeLeadFormStartEventPropertiesParams,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../application/tracking/amplitude/track_amplitude_lead_form_events";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {AmplitudeAuthLocation} from "../../auth/tracking/track_amplitude_auth_event";
import {AmplitudeReservationButtonName, trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {AmplitudeReservationModalName, trackAmplitudeViewModal} from "../../tracking/amplitude/track_amplitude_view_modal";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {getAmplitudeScreenLocation} from "../../user/tracking/track_amplitude_phone_verified";
import {ViewType} from "../../view_type/ViewType";
import {RESERVATION_CONFIRMED_BUTTON_LABEL} from "../constants/reservation_copy";
import {getReservationEntry} from "../constants/reservation_placement_config";
import {IReservationError} from "../types/IReservationError";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationStepName} from "../types/ReservationStepName";
import {getReservationAuthPlacement} from "./get_reservation_auth_placement";
import {gtmReservation, gtmReservationStart} from "./gtm_reservation";

/*
 * Every step of the free reservation funnel is reported to Amplitude and GTM by one call.
 * The application is created by the backend on confirm, so the lead form events are reported here and not by the lead form.
 * A reservation started from the unlock choice modal is reported by track_unlock_choice_reservation_events instead - get_reservation_tracker picks the funnel.
 */

const RESERVATION_SOURCE = ApplicationSource.PropertyVoxOnlineReservation;
const RESERVATION_SOURCE_SECTION = ApplicationSourceSection.MODAL;
const RESERVATION_CTA_TEXT = InquiryButtonTypeName.FREE_RESERVATION;

export interface IReservationTrackingContext {
    // the placement of the registration that led to this funnel; when omitted, the placement of the reservation button is used
    authPlacement?: AmplitudeAuthPlacement | null;
    // the location the registration flow was opened with, reported by the unlock choice funnel instead of the location of the view type
    amplitudeLocation?: AmplitudeAuthLocation;
    viewType?: ViewType | null;
    // vendor, offer and property details for `products` of the lead form events - without them `products` is empty
    leadData?: IAmplitudeLeadFormProductData | null;
}

export interface IReservationStartTrackingContext extends IReservationTrackingContext {
    // reported by pre_form_open of the buttons on the property page
    offerName?: string | null;
}

type ReservationLeadFormParams = Pick<IGetAmplitudeLeadFormStartEventPropertiesParams, "trackingContext" | "leadData" | "source" | "sourceSection">;

const getAuthPlacement = (request: IReservationRequest, context: IReservationTrackingContext): AmplitudeAuthPlacement | null =>
    getReservationAuthPlacement(request.placement, context.authPlacement);

// which flow and which button the funnel was started from - on every Amplitude event of the funnel
const getReservationFunnelProperties = (request: IReservationRequest) => ({
    reservation_entry: getReservationEntry(request),
    placement: request.placement
});

const trackReservationButtonClick = (
    buttonName: AmplitudeReservationButtonName,
    request: IReservationRequest,
    context: IReservationTrackingContext,
    value: string | null = null
) => {
    trackAmplitudeButtonClick({
        buttonName,
        buttonCategory: "rp_reservation",
        itemId: request.offerId,
        propertyId: request.propertyId,
        value,
        additionalEventProperties: {
            ...getReservationFunnelProperties(request),
            vendor_id: request.vendorId,
            auth_placement: getAuthPlacement(request, context)
        }
    });
};

const trackReservationModalView = (modalName: AmplitudeReservationModalName, request: IReservationRequest, context: IReservationTrackingContext) => {
    trackAmplitudeViewModal({
        modalName,
        location: getAmplitudeScreenLocation(context.viewType ?? null),
        authPlacement: getAuthPlacement(request, context),
        itemId: request.offerId,
        propertyId: request.propertyId,
        additionalEventProperties: {
            ...getReservationFunnelProperties(request),
            vendor_id: request.vendorId
        }
    });
};

// The lead form events of the funnel - none without an offer to report them for
const trackReservationLeadFormEvent = (
    request: IReservationRequest,
    context: IReservationTrackingContext,
    track: (params: ReservationLeadFormParams) => void
) => {
    const trackingContext = getAmplitudeCtaLeadFormTrackingContext({
        source: RESERVATION_SOURCE,
        sourceSection: RESERVATION_SOURCE_SECTION,
        leadData: undefined,
        ctaText: RESERVATION_CTA_TEXT,
        offerId: request.offerId,
        propertyId: request.propertyId,
        viewType: context.viewType ?? null,
        formLocation: "rp_reservation"
    });

    if (trackingContext) {
        track({
            trackingContext: {
                ...trackingContext,
                auth_placement: getAuthPlacement(request, context),
                additionalEventProperties: getReservationFunnelProperties(request)
            },
            leadData: context.leadData ?? undefined,
            source: RESERVATION_SOURCE,
            sourceSection: RESERVATION_SOURCE_SECTION
        });
    }
};

// The click of a reservation button on the property page or in the post-lead modal
export const trackReservationStart = (request: IReservationRequest, context: IReservationStartTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_start", request, context, request.placement);
    gtmReservationStart(request, context.offerName ?? null);
};

// Before the first initiate, so that an initiate failure has a lead_form_start before it
export const trackReservationLeadFormStart = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, trackAmplitudeLeadFormStart);
};

// The first code has been sent
export const trackReservationCodeView = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationModalView("reservation_code", request, context);
    gtmReservation("view", request.placement);
};

// The last digit has been typed in - the code is submitted without a button
export const trackReservationCodeSubmit = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_code_submit", request, context);
    gtmReservation("code_submit", request.placement);
};

// `codesSent` - codes sent before this resend
export const trackReservationCodeResend = (request: IReservationRequest, codesSent: number, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_code_resend", request, context, String(codesSent));
    gtmReservation("resend", request.placement, String(codesSent));
};

// `applicationUuid` - null when the confirm response came without it; the reservation is reported all the same
export const trackReservationGenerateLead = (request: IReservationRequest, applicationUuid: string | null, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, (params) =>
        trackAmplitudeLeadFormGenerateLead({
            ...params,
            ...(applicationUuid === null ? {} : {transactionId: applicationUuid}),
            // the reservation screen collects no marketing consents
            marketingApproval: false,
            inquirySelection: {}
        })
    );

    trackReservationModalView("reservation_confirmed", request, context);
    gtmReservation("success", request.placement);
};

export const trackReservationError = (request: IReservationRequest, error: IReservationError, context: IReservationTrackingContext = {}) => {
    trackReservationLeadFormEvent(request, context, (params) =>
        trackAmplitudeLeadFormError({
            ...params,
            // built from the normalized error - `getAppErrors` would report each of its keys as a form field
            appError: {
                status: typeof error.raw.status === "number" ? error.raw.status : 0,
                fieldErrors: {},
                nonFieldErrors: [error.message]
            },
            errorCode: `${error.kind}:${error.raw.status}`
        })
    );

    gtmReservation("error", request.placement, error.kind);
};

// The close button or Esc of the code step, the overlay or Esc of the confirmation (not its "ROZUMIEM" button)
export const trackReservationClose = (request: IReservationRequest, step: ReservationStepName, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_close", request, context, step);
    gtmReservation("close", request.placement, step);
};

export const trackReservationConfirmedClose = (request: IReservationRequest, context: IReservationTrackingContext = {}) => {
    trackReservationButtonClick("rp_reservation_confirmed_close", request, context);
    gtmReservation("close", request.placement, RESERVATION_CONFIRMED_BUTTON_LABEL);
};
